<template>
  <v-select
    :model-value="modelValue"
    :items="filteredItems"
    :disabled="disabled"
    :menu-props="{ maxHeight: '300' }"
    class="filter-menu"
    :class="{ 'filter-active': modelValue?.length }"
    :placeholder="$t('tickets.filters.type')"
    :data-testid="dataTestId"
    density="compact"
    variant="outlined"
    multiple
    rounded
    hide-details
    bg-color="white"
    :item-value="getTriggerKey"
    :item-title="getTriggerKey"
    @update:model-value="$emit('update:modelValue', $event)"
    @update:menu="search = ''"
  >
    <template #selection="{ index }">
      <span v-if="index === 0" class="body2">{{ $t("tickets.filters.type") }}</span>
    </template>
    <template #label>
      <span v-if="modelValue?.length" class="filter-label">{{ modelValue.length }}</span>
    </template>
    <template #prepend-item>
      <cui-select-search-field
        v-model="search"
        :placeholder="$t('general.search')"
        :data-test-id="`${dataTestId}-search`"
      />
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-subheader
        v-if="item.raw.subheader || item.raw.type === 'subheader'"
        :title="getTriggerKey(item.raw)"
      />
      <v-list-item v-else v-bind="itemProps" title="">
        <template #prepend="{ isActive }">
          <v-list-item-action start>
            <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
          </v-list-item-action>
        </template>

        <v-list-item-title>
          {{ $t(`tickets.eventTriggers.${getTriggerKey(item.raw)}`) }}
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { CuiSelectSearchField, useCuiSelectSearch } from "@coronet/cui";
import { type TriggerItem } from "@/constants/tickets";

/**
 * Tickets "Type" (event triggers) multi-select with an in-dropdown search.
 * Consolidates the two hand-rolled copies (TicketsFilters + ScheduledExportModalFilters).
 * The parent resolves and passes the `triggers` list; item keys read `name ?? value`
 * so both item shapes (`{value}` and `{name}`) are supported.
 */
export default defineComponent({
  components: { CuiSelectSearchField },
  props: {
    /** Selected trigger keys (v-model). */
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** Resolved trigger list to render (subheader rows + trigger rows); built by the parent. */
    triggers: {
      type: Array as PropType<TriggerItem[]>,
      default: () => [],
    },
    /** Disables the select. */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** `data-testid` for the select; the search field derives `${dataTestId}-search`. */
    dataTestId: {
      type: String,
      default: "ticket-type-filter",
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const { t } = useI18n();

    const getTriggerKey = (item: TriggerItem) => item.name ?? item.value ?? "";

    const isSubheader = (item: TriggerItem) => item.subheader === true || item.type === "subheader";

    const { search, filteredItems } = useCuiSelectSearch<TriggerItem>(
      () => props.triggers,
      (item) => t(`tickets.eventTriggers.${getTriggerKey(item)}`),
      { isSubheader }
    );

    return { search, filteredItems, getTriggerKey };
  },
});
</script>
